import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import {
  AlertCircle,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Mail,
  MoreVertical,
  PackageSearch,
  RefreshCw,
  Search,
  Sparkles,
  Store,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import ConfirmAlertDialog from '@/components/ui/confirm-alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useToast } from '@/hooks/use-toast';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { useAppSelector } from '@/app/hooks';
import { selectCurrentService } from '@/features/service/serviceSlice';
import { openEtsyOrderInProductionFlow } from '@/components/etsy/openEtsyOrderInProductionFlow';
import {
  fetchConnectedEtsyShops,
  deleteEtsyImportedOrder,
  fetchEtsyImportedItemImage,
  fetchPendingEtsyOrders,
  type EtsyInboxQueueStatus,
  type EtsyPendingOrder,
} from '@/services/etsyService';
import noImageWatercolor from '@/assets/images/no-image-watercolor.svg';
import EtsyWorkspaceNav from '@/components/etsy/EtsyWorkspaceNav';
import { WORKSPACE_CANVAS_MODES } from '@/components/workspace/workspaceMode';
import { getPreferredEtsyItemPreviewUrl } from '@/components/etsy/etsyPreview';
import {
  getEtsyProductionWarningSummary,
  getEtsyProductionWarnings,
} from '@/components/etsy/etsyProductionWarnings';

const formatDateTime = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatMoney = (order: EtsyPendingOrder) => {
  const amount = order.etsyData.grandtotal?.amount;
  const divisor = order.etsyData.grandtotal?.divisor;
  const currency = order.etsyData.grandtotal?.currency_code;

  if (!amount || !divisor || !currency) {
    return order.etsyData.total_price || 'n/a';
  }

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount / divisor);
  } catch {
    return `${currency} ${(amount / divisor).toFixed(2)}`;
  }
};

const formatProductionSize = (
  item: EtsyPendingOrder['items'][number],
  t: TFunction,
) => {
  if (item.matchedWidthIn == null || item.matchedHeightIn == null) {
    return t('integrations.etsy.common.notMatchedYet', 'Not matched yet');
  }

  return `${item.matchedWidthIn}" x ${item.matchedHeightIn}"`;
};

const formatProductionPaper = (
  item: EtsyPendingOrder['items'][number],
  t: TFunction,
) => {
  if (!item.matchedPaperType) {
    return t('integrations.etsy.common.notMatchedYet', 'Not matched yet');
  }

  return `${item.matchedPaperType.brand} - ${item.matchedPaperType.name}`;
};

const getAddressSummary = (order: EtsyPendingOrder) => {
  if (order.etsyData.formatted_address?.trim()) {
    return order.etsyData.formatted_address.trim();
  }

  const streetParts = [
    order.etsyData.first_line,
    order.etsyData.second_line,
  ].filter((part): part is string => Boolean(part?.trim()))
    .map((part) => part.trim());

  if (streetParts.length > 0) {
    return streetParts.join(', ');
  }

  const parts = [
    order.etsyData.city,
    order.etsyData.state,
    order.etsyData.zip,
    order.etsyData.country_iso,
  ].filter((part): part is string => Boolean(part?.trim()))
    .map((part) => part.trim());

  return parts.join(', ');
};

const getTransactionCount = (order: EtsyPendingOrder) =>
  Array.isArray(order.etsyData.transactions) ? order.etsyData.transactions.length : 0;

const getFullScaleImageStatusLabel = (
  item: EtsyPendingOrder['items'][number],
  t: TFunction,
) => (item.fullSizeUploadedFile
  ? t('integrations.etsy.images.fullScaleUploaded', 'Full-scale image uploaded')
  : t('integrations.etsy.images.fullScaleMissing', 'Full-scale image missing'));

const getImageActionLabel = (
  imageAvailability: EtsyPendingOrder['items'][number]['imageAvailability'],
  t: TFunction,
) => {
  if (imageAvailability.status === 'failed') {
    return t('integrations.etsy.images.retryDraftUpload', 'Retry image upload');
  }

  if (imageAvailability.status === 'missing') {
    return t('integrations.etsy.images.fetchDraftImage', 'Fetch draft image');
  }

  return null;
};

const isItemReadyForProductionSetup = (item: EtsyPendingOrder['items'][number]) =>
  Boolean(item.fullSizeUploadedFile)
  && Boolean(item.matchedPaperType)
  && item.paperReviewStatus === 'CONFIRMED'
  && item.matchedWidthIn != null
  && item.matchedHeightIn != null;

const isOrderReadyForProductionSetup = (order: EtsyPendingOrder) =>
  order.items.length > 0
  && order.addressReviewStatus === 'CONFIRMED'
  && order.items.every(isItemReadyForProductionSetup);

const getReadinessState = (order: EtsyPendingOrder): 'review' | 'image' | 'matching' | 'ready' => {
  if (getTransactionCount(order) === 0) {
    return 'review';
  }

  if (!order.imageDownloaded) {
    return 'image';
  }

  return isOrderReadyForProductionSetup(order) ? 'ready' : 'matching';
};

const getReadinessLabel = (order: EtsyPendingOrder, t: TFunction) => {
  switch (getReadinessState(order)) {
    case 'review':
      return t('integrations.etsy.readiness.needsReview', 'Needs item review');
    case 'image':
      return t('integrations.etsy.readiness.needsDraftImage', 'Needs draft image');
    case 'matching':
      return t('integrations.etsy.readiness.readyToMatching', 'Ready to matching');
    case 'ready':
      return t('integrations.etsy.readiness.readyForProduction', 'Ready for production setup');
    default:
      return t('integrations.etsy.readiness.new', 'New');
  }
};

const getDraftBadgeVariant = (
  status: NonNullable<EtsyPendingOrder['workspaceDraft']>['status'],
): 'default' | 'secondary' | 'outline' => {
  switch (status) {
    case 'ACTIVE':
      return 'default';
    case 'PARKED':
      return 'secondary';
    default:
      return 'outline';
  }
};

const getDraftStatusLabel = (order: EtsyPendingOrder, t: TFunction) => {
  if (!order.workspaceDraft) {
    return t('integrations.etsy.draft.notStarted', 'Production not started');
  }

  switch (order.workspaceDraft.status) {
    case 'ACTIVE':
      return t('integrations.etsy.draft.active', 'In production setup');
    case 'PARKED':
      return t('integrations.etsy.draft.parked', 'Production draft saved');
    case 'COMPLETED':
      return t('integrations.etsy.draft.completed', 'Production draft completed');
    case 'ARCHIVED':
      return t('integrations.etsy.draft.archived', 'Archived');
    default:
      return t('integrations.etsy.draft.exists', 'Draft exists');
  }
};

const shouldShowDraftStatus = (order: EtsyPendingOrder) =>
  Boolean(order.workspaceDraft) && isPendingQueueStatus(order.queueStatus);

const buildSearchIndex = (order: EtsyPendingOrder) =>
  [
    order.etsyReceiptId,
    order.shopName,
    order.etsyData.name,
    order.etsyData.buyer_email,
    order.etsyData.city,
    order.etsyData.country_iso,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

type InboxMetric = {
  readonly label: string;
  readonly value: number;
  readonly tone: 'default' | 'secondary' | 'outline';
};

type InboxFilter = 'pending' | 'fulfilled' | 'all';
type DetailTagTone = 'neutral' | 'success' | 'info' | 'warning';

const getDetailTagClassName = (tone: DetailTagTone = 'neutral') => {
  const baseClassName = 'rounded-[6px] border px-2 py-0.5 text-[11px] font-medium shadow-none';

  switch (tone) {
    case 'success':
      return `${baseClassName} border-emerald-200 bg-emerald-50 text-emerald-800 hover:bg-emerald-50 dark:border-emerald-900/60 dark:bg-emerald-950/30 dark:text-emerald-200`;
    case 'info':
      return `${baseClassName} border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-50 dark:border-sky-900/60 dark:bg-sky-950/30 dark:text-sky-200`;
    case 'warning':
      return `${baseClassName} border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-50 dark:border-amber-900/60 dark:bg-amber-950/30 dark:text-amber-200`;
    case 'neutral':
    default:
      return `${baseClassName} border-border bg-muted/35 text-muted-foreground hover:bg-muted/35`;
  }
};

const getPreviewGridClass = (count: number) => {
  if (count <= 1) {
    return 'grid-cols-1';
  }
  if (count === 2) {
    return 'grid-cols-2';
  }
  return 'grid-cols-2 grid-rows-2';
};

export const EtsyOrderPreviewThumbnail = ({
  order,
  className,
}: {
  order: EtsyPendingOrder;
  className?: string;
}) => {
  const { t } = useTranslation();
  const previewItems = order.items.slice(0, 4);
  const previewGridClass = getPreviewGridClass(previewItems.length || 1);
  const extraPreviewCount = Math.max(order.items.length - previewItems.length, 0);

  const content = (
    <>
      <div className={`grid h-full w-full gap-0.5 ${previewGridClass}`}>
        {previewItems.length > 0 ? previewItems.map((item) => (
          <div key={item.transactionId} className="flex h-full w-full items-center justify-center rounded-sm bg-background/80 p-[1px]">
            <img
              key={getPreferredEtsyItemPreviewUrl(item) || 'no-image'}
              src={getPreferredEtsyItemPreviewUrl(item) || noImageWatercolor}
              alt={item.title}
              className="h-full w-full rounded-[2px] object-cover"
              loading="lazy"
            />
          </div>
        ) ) : (
          <div className="flex h-full w-full items-center justify-center rounded-sm bg-background/80 p-[1px]">
            <img
              src={noImageWatercolor}
              alt={t('integrations.etsy.images.noDraftImageAlt', 'No Etsy draft image available')}
              className="h-full w-full rounded-[2px] object-cover"
              loading="lazy"
            />
          </div>
        )}
      </div>
      {extraPreviewCount > 0 ? (
        <span className="absolute right-1 top-1 rounded-full bg-background/90 px-1.5 py-0.5 text-xs font-semibold text-foreground shadow-sm">
          +{extraPreviewCount}
        </span>
      ) : null}
    </>
  );

  return (
    <div className={`relative flex h-16 w-24 items-center justify-center overflow-hidden rounded-lg border border-border/60 bg-muted/20 ${className ?? ''}`}>
      {content}
    </div>
  );
};

const isPendingQueueStatus = (status: EtsyInboxQueueStatus) =>
  status === 'PENDING' || status === 'IN_PRODUCTION';

const getQueueStatusLabel = (status: EtsyInboxQueueStatus, t: TFunction) => {
  switch (status) {
    case 'PENDING':
      return t('integrations.etsy.queue.pending', 'Pending');
    case 'IN_PRODUCTION':
      return t('integrations.etsy.queue.inProduction', 'In production');
    case 'CONFIRMED':
      return t('integrations.etsy.queue.confirmed', 'Confirmed');
    case 'FULFILLED':
      return t('integrations.etsy.queue.fulfilled', 'Fulfilled');
    default:
      return status;
  }
};

const getQueueStatusBadgeVariant = (
  status: EtsyInboxQueueStatus,
): 'default' | 'secondary' | 'outline' => {
  switch (status) {
    case 'PENDING':
      return 'outline';
    case 'IN_PRODUCTION':
      return 'secondary';
    case 'CONFIRMED':
    case 'FULFILLED':
      return 'default';
    default:
      return 'outline';
  }
};

const getQueueStatusTagTone = (status: EtsyInboxQueueStatus): DetailTagTone => {
  switch (status) {
    case 'CONFIRMED':
    case 'FULFILLED':
      return 'success';
    case 'IN_PRODUCTION':
      return 'info';
    case 'PENDING':
    default:
      return 'neutral';
  }
};

const getDraftStatusTagTone = (
  status: NonNullable<EtsyPendingOrder['workspaceDraft']>['status'],
): DetailTagTone => {
  switch (status) {
    case 'ACTIVE':
      return 'info';
    case 'PARKED':
      return 'warning';
    case 'COMPLETED':
      return 'success';
    default:
      return 'neutral';
  }
};

type MissingArea = 'papers' | 'sizes' | 'addresses' | 'images';

const getMissingAreas = (order: EtsyPendingOrder): MissingArea[] => {
  const missing: MissingArea[] = [];

  if (!order.items.every((item) => item.matchedPaperType && item.paperReviewStatus === 'CONFIRMED')) {
    missing.push('papers');
  }

  if (!order.items.every((item) => item.matchedWidthIn != null && item.matchedHeightIn != null)) {
    missing.push('sizes');
  }

  if (!(order.matchedAddress && order.addressReviewStatus === 'CONFIRMED')) {
    missing.push('addresses');
  }

  if (!order.items.every((item) => item.fullSizeUploadedFile != null)) {
    missing.push('images');
  }

  return missing;
};

const getMissingAreaLabel = (area: MissingArea, translate: TFunction) => (
  area === 'papers'
    ? translate('integrations.etsy.toProduction.summary.papers', 'Papers')
    : area === 'sizes'
      ? translate('integrations.etsy.toProduction.summary.sizes', 'Sizes')
      : area === 'addresses'
        ? translate('integrations.etsy.toProduction.summary.addresses', 'Address')
        : translate('integrations.etsy.toProduction.summary.images', 'Full-size image')
);

const getOrderPrimaryStatus = (
  order: EtsyPendingOrder,
  t: TFunction,
): {
  label: string;
  variant: 'default' | 'secondary' | 'outline';
  tone: DetailTagTone;
} => {
  if (order.workspaceDraft && isPendingQueueStatus(order.queueStatus)) {
    return {
      label: getDraftStatusLabel(order, t),
      variant: getDraftBadgeVariant(order.workspaceDraft.status),
      tone: getDraftStatusTagTone(order.workspaceDraft.status),
    };
  }

  if (order.queueStatus !== 'PENDING') {
    return {
      label: getQueueStatusLabel(order.queueStatus, t),
      variant: getQueueStatusBadgeVariant(order.queueStatus),
      tone: getQueueStatusTagTone(order.queueStatus),
    };
  }

  return {
    label: getReadinessLabel(order, t),
    variant: getReadinessState(order) === 'review' ? 'outline' : 'secondary',
    tone: getReadinessState(order) === 'ready' ? 'success' : 'neutral',
  };
};

export const EtsyOrdersCanvas = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currentService = useAppSelector(selectCurrentService);
  const [orders, setOrders] = useState<ReadonlyArray<EtsyPendingOrder>>([]);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<ReadonlyArray<string>>([]);
  const [currentFetchingImageItemId, setCurrentFetchingImageItemId] = useState<string | null>(null);
  const [queuedImageFetches, setQueuedImageFetches] = useState<ReadonlyArray<{
    readonly itemId: string;
    readonly trigger: 'manual' | 'auto';
  }>>([]);
  const [deletingOrderIds, setDeletingOrderIds] = useState<ReadonlyArray<string>>([]);
  const [confirmOrder, setConfirmOrder] = useState<EtsyPendingOrder | null>(null);
  const [openingOrderId, setOpeningOrderId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<InboxFilter>('pending');
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const autoAttemptedImageItemIdsRef = useRef<Set<string>>(new Set());

  const loadOrders = async () => {
    try {
      setStatus('loading');
      setError(null);
      const result = await fetchPendingEtsyOrders({ includeCompleted: true });
      setOrders(result);
      setSelectedOrderId((current) => {
        if (current && result.some((order) => order.id === current)) {
          return current;
        }
        return result[0]?.id ?? null;
      });
      setExpandedOrderIds((current) => {
        const kept = current.filter((orderId) => result.some((order) => order.id === orderId));
        return kept;
      });
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to load Etsy pending orders', { error: err });
      setError(err instanceof Error ? err.message : t('integrations.etsy.ordersLoadFailed', 'Failed to load Etsy orders.'));
      setStatus('error');
    }
  };

  useEffect(() => {
    void loadOrders();
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadConnectedShops = async () => {
      try {
        const shops = await fetchConnectedEtsyShops();
        if (!cancelled && shops.length === 0) {
          analytics.track('button: ui.etsy_setup_auto_redirect', { location: 'etsy_orders_inbox' });
          navigate('/studio?canvas=etsy-setup');
        }
      } catch (err) {
        logging.warn('Failed to check connected Etsy shops for orders inbox', { error: err });
      }
    };

    void loadConnectedShops();

    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleFetchOrderImages = async (order: EtsyPendingOrder) => {
    const missingItems = order.items.filter((item) => item.imageAvailability.status !== 'ready');
    if (missingItems.length === 0) {
      toast({
        title: t('integrations.etsy.toasts.allImagesReadyTitle', 'Images already available'),
        description: t('integrations.etsy.toasts.allImagesReadyDescription', 'All items in this order already have preview images.'),
      });
      return;
    }

    try {
      toast({
        title: t('integrations.etsy.toasts.fetchingImagesTitle', 'Fetching draft images'),
        description: t('integrations.etsy.toasts.fetchingImagesDescription', 'TulasiHome started downloading {{count}} listing images from Etsy.', { count: missingItems.length }),
      });

      const results = await Promise.all(missingItems.map(async (item) => {
        const result = await fetchEtsyImportedItemImage(item.id);
        if (result.imageAvailability.status !== 'ready') {
          console.warn(`[EtsyOrders] Failed to fetch image for item ${item.id}:`, result.imageAvailability.note);
        }
        return { itemId: item.id, ...result };
      }));
      
      // Update local state immediately with new URLs to avoid UI lag
      setOrders(current => current.map(o => {
        if (o.id !== order.id) return o;
        return {
          ...o,
          items: o.items.map(item => {
            const result = results.find(r => r.itemId === item.id);
            if (!result) return item;
            return {
              ...item,
              previewUrl: result.previewUrl,
              imageAvailability: result.imageAvailability
            };
          })
        };
      }));

      toast({
        title: t('integrations.etsy.toasts.fetchImagesFinishedTitle', 'Images downloaded'),
        description: t('integrations.etsy.toasts.fetchImagesFinishedDescription', 'Successfully updated preview images for the order.'),
      });
      
      await loadOrders();
    } catch (error) {
      logging.error('Failed to fetch order images', { error, receiptId: order.etsyReceiptId });
      toast({
        title: t('integrations.etsy.toasts.fetchImagesFailedTitle', 'Could not download images'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    }
  };

  const handleOpenInProduction = async (order: EtsyPendingOrder) => {
    try {
      setOpeningOrderId(order.id);
      await openEtsyOrderInProductionFlow({
        importedOrderId: order.id,
        etsyReceiptId: order.etsyReceiptId,
        currentServiceId: currentService?.id ?? null,
        navigate,
      });
    } catch (err) {
      toast({
        title: t('integrations.etsy.toasts.openInProductionFailed', 'Failed to open in production'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setOpeningOrderId(null);
      setConfirmOrder(null);
    }
  };

  const requestOpenInProduction = (order: EtsyPendingOrder) => {
    const missing = getMissingAreas(order);
    const warnings = getEtsyProductionWarnings(order);
    if (missing.length > 0 || warnings.length > 0) {
      setConfirmOrder(order);
      return;
    }
    void handleOpenInProduction(order);
  };

  const filteredOrders = useMemo(() => {
    // Only show orders that have been explicitly moved to inbox from the Import tab
    const inboxOrders = orders.filter((order) => order.selectedForInbox);

    const statusFilteredOrders = inboxOrders.filter((order) => {
      switch (filter) {
        case 'pending':
          return isPendingQueueStatus(order.queueStatus);
        case 'fulfilled':
          return !isPendingQueueStatus(order.queueStatus);
        case 'all':
        default:
          return true;
      }
    });

    const normalized = query.trim().toLowerCase();
    if (!normalized) {
      return statusFilteredOrders;
    }

    return statusFilteredOrders.filter((order) => buildSearchIndex(order).includes(normalized));
  }, [filter, orders, query]);

  const selectedOrder = useMemo(() => {
    const found = filteredOrders.find((o) => o.id === selectedOrderId);
    if (found) return found;
    return filteredOrders[0] ?? null;
  }, [filteredOrders, selectedOrderId]);

  const selectedOrderDraft = selectedOrder?.workspaceDraft ?? null;

  useEffect(() => {
    if (!selectedOrder) {
      return;
    }

    console.log('[etsy-order-input]', {
      importedOrderId: selectedOrder.id,
      etsyReceiptId: selectedOrder.etsyReceiptId,
      shopName: selectedOrder.shopName,
      destination: {
        formattedAddress: selectedOrder.etsyData.formatted_address ?? null,
        firstLine: selectedOrder.etsyData.first_line ?? null,
        secondLine: selectedOrder.etsyData.second_line ?? null,
        city: selectedOrder.etsyData.city ?? null,
        state: selectedOrder.etsyData.state ?? null,
        zip: selectedOrder.etsyData.zip ?? null,
        countryIso: selectedOrder.etsyData.country_iso ?? null,
      },
      items: selectedOrder.items.map((item) => ({
        importedItemId: item.id,
        transactionId: item.transactionId,
        listingId: item.listingId,
        title: item.title,
        quantity: item.quantity,
        sku: item.sku,
        likelySize: item.sizeSelection,
        likelyMaterial: item.materialSelection,
        variationSummary: item.variationSummary,
        imageAvailability: item.imageAvailability,
        previewUrl: item.previewUrl,
      })),
    });
  }, [selectedOrder]);

  useEffect(() => {
    if (!selectedOrder && filteredOrders.length > 0) {
      setSelectedOrderId(filteredOrders[0].id);
    }
  }, [filteredOrders, selectedOrder]);

  const metrics = useMemo<ReadonlyArray<InboxMetric>>(() => {
    const reviewCount = orders.filter((order) => getReadinessState(order) === 'review').length;
    const imageCount = orders.filter((order) => getReadinessState(order) === 'image').length;

    return [
      {
        label: t('integrations.etsy.metric.totalImported', 'Imported queue'),
        value: orders.length,
        tone: 'default',
      },
      {
        label: t('integrations.etsy.metric.needsReview', 'Needs review'),
        value: reviewCount,
        tone: 'outline',
      },
      {
        label: t('integrations.etsy.metric.needsDraftImage', 'Needs draft image'),
        value: imageCount,
        tone: 'secondary',
      },
    ];
  }, [orders, t]);

  const toggleOrderExpansion = (orderId: string) => {
    setSelectedOrderId(orderId);
    setExpandedOrderIds((current) => (
      current.includes(orderId)
        ? current.filter((id) => id !== orderId)
        : [...current, orderId]
    ));
  };

  const handleFetchItemImage = useCallback(async (
    itemId: string,
    trigger: 'manual' | 'auto' = 'manual',
  ) => {
    try {
      analytics.track(
        trigger === 'manual' ? 'button: ui.etsy_fetch_item_image' : 'system: ui.etsy_auto_fetch_item_image',
        { importedItemId: itemId },
      );
      await fetchEtsyImportedItemImage(itemId);
      await loadOrders();
    } catch (error) {
      logging.error('Failed to fetch Etsy imported item image', { error, importedItemId: itemId });
      try {
        await loadOrders();
      } catch (reloadError) {
        logging.error('Failed to reload Etsy orders after image fetch error', {
          error: reloadError,
          importedItemId: itemId,
        });
      }
    } finally {
      setCurrentFetchingImageItemId(null);
    }
  }, []);

  useEffect(() => {
    if (currentFetchingImageItemId || queuedImageFetches.length === 0) {
      return;
    }

    const [nextRequest, ...rest] = queuedImageFetches;
    setQueuedImageFetches(rest);
    setCurrentFetchingImageItemId(nextRequest.itemId);
    void handleFetchItemImage(nextRequest.itemId, nextRequest.trigger);
  }, [currentFetchingImageItemId, handleFetchItemImage, queuedImageFetches]);

  useEffect(() => {
    if (status !== 'idle' || currentFetchingImageItemId || queuedImageFetches.length > 0 || orders.length === 0) {
      return;
    }

    const nextMissingItem = orders
      .flatMap((order) => order.items)
      .find((item) => (
        item.imageAvailability.status === 'missing'
        && !autoAttemptedImageItemIdsRef.current.has(item.id)
      ));

    if (!nextMissingItem) {
      return;
    }

    autoAttemptedImageItemIdsRef.current.add(nextMissingItem.id);
    setQueuedImageFetches((current) => [...current, { itemId: nextMissingItem.id, trigger: 'auto' }]);
  }, [currentFetchingImageItemId, orders, queuedImageFetches.length, status]);

  const requestImageFetch = useCallback((itemId: string, trigger: 'manual' | 'auto' = 'manual') => {
    if (currentFetchingImageItemId === itemId || queuedImageFetches.some((request) => request.itemId === itemId)) {
      return;
    }

    if (trigger === 'manual') {
      setQueuedImageFetches((current) => [
        { itemId, trigger: 'manual' },
        ...current.filter((request) => request.itemId !== itemId),
      ]);
      return;
    }

    setQueuedImageFetches((current) => [...current, { itemId, trigger: 'auto' }]);
  }, [currentFetchingImageItemId, queuedImageFetches]);

  const handleDeleteImportedOrder = async (order: EtsyPendingOrder) => {
    try {
      setDeletingOrderIds((current) => (current.includes(order.id) ? current : [...current, order.id]));
      analytics.track('dropdown: ui.etsy_delete_imported_order', {
        importedOrderId: order.id,
        etsyReceiptId: order.etsyReceiptId,
      });
      await deleteEtsyImportedOrder(order.id);
      await loadOrders();
    } catch (error) {
      logging.error('Failed to delete imported Etsy order', {
        error,
        importedOrderId: order.id,
        etsyReceiptId: order.etsyReceiptId,
      });
    } finally {
      setDeletingOrderIds((current) => current.filter((currentId) => currentId !== order.id));
    }
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <Card className="border-border/70">
        <CardHeader className="gap-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div className="space-y-2">
              <div className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/20 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <Store className="h-3.5 w-3.5" />
                {t('integrations.etsy.ordersInboxBadge', 'Etsy intake')}
              </div>
              <div>
                <CardTitle className="text-xl">
                  {t('integrations.etsy.ordersInboxTitle', 'Etsy Orders Inbox')}
                </CardTitle>
                <CardDescription className="mt-1 max-w-3xl">
                  {t(
                    'integrations.etsy.ordersInboxDescription',
                    'Handle Etsy imports in a dedicated intake queue before they become normal internal orders. This first version focuses on a dense table and guided review alongside the AI chat.'
                  )}
                </CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative min-w-[260px] flex-1">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder={t('integrations.etsy.ordersSearch', 'Search by receipt, buyer, shop, or destination')}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={() => void loadOrders()} disabled={status === 'loading'}>
                {status === 'loading' ? (
                  <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                {t('common.reload', 'Reload')}
              </Button>
            </div>
          </div>

          <EtsyWorkspaceNav activeMode={WORKSPACE_CANVAS_MODES.etsyOrders} />

          <div className="grid gap-3 md:grid-cols-3">
            {metrics.map((metric) => (
              <div key={metric.label} className="rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-medium text-foreground">{metric.label}</div>
                  <Badge variant={metric.tone}>{metric.value}</Badge>
                </div>
              </div>
            ))}
          </div>
        </CardHeader>
      </Card>
      <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1.65fr)_minmax(340px,0.85fr)]">
        <Card className="min-h-0 border-border/70">
          <CardHeader className="flex flex-row items-center justify-between gap-3">
            <div>
              <CardTitle className="text-lg">
                {t('integrations.etsy.ordersTableTitle', 'Imported orders')}
              </CardTitle>
              <CardDescription>
                {t('integrations.etsy.ordersVisible', '{{count}} order visible', {
                  count: filteredOrders.length,
                  defaultValue_plural: '{{count}} orders visible',
                })}
              </CardDescription>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <Button
                type="button"
                variant={filter === 'pending' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('pending')}
              >
                {t('integrations.etsy.filters.pending', 'Pending')}
              </Button>
              <Button
                type="button"
                variant={filter === 'fulfilled' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('fulfilled')}
              >
                {t('integrations.etsy.filters.fulfilled', 'Fulfilled')}
              </Button>
              <Button
                type="button"
                variant={filter === 'all' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                {t('integrations.etsy.filters.all', 'All')}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="min-h-0 pb-4">
            {status === 'loading' && orders.length === 0 ? (
              <div className="flex items-center justify-center py-16 text-muted-foreground">
                <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                {t('common.loading', 'Loading')}
              </div>
            ) : null}

            {error && orders.length === 0 ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
                {error}
              </div>
            ) : null}

            {!error && filteredOrders.length === 0 && status !== 'loading' ? (
              <div className="rounded-xl border border-dashed border-border bg-muted/20 px-6 py-14 text-center">
                <PackageSearch className="mx-auto mb-4 h-10 w-10 text-muted-foreground" />
                <h2 className="text-lg font-semibold text-foreground">
                  {t('integrations.etsy.ordersEmptyTitle', 'Your inbox is empty')}
                </h2>
                <p className="mt-2 text-sm text-muted-foreground text-center max-w-sm">
                  {t(
                    'integrations.etsy.ordersEmptyDescription',
                    'Go to the "Import" tab, select the orders you want to process, and click "Move selected to Inbox". Only selected orders will appear here for matching.'
                  )}
                </p>
              </div>
            ) : null}

            {filteredOrders.length > 0 ? (
              <ScrollArea className="h-[calc(100vh-21rem)] rounded-lg border border-border/60">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead />
                      <TableHead>#</TableHead>
                      <TableHead>{t('integrations.etsy.ordersTable.preview', 'Preview')}</TableHead>
                      <TableHead>{t('integrations.etsy.ordersTable.imported', 'Imported')}</TableHead>
                      <TableHead>{t('integrations.etsy.ordersTable.status', 'Status')}</TableHead>
                      <TableHead>{t('integrations.etsy.ordersTable.destination', 'Destination')}</TableHead>
                      <TableHead className="text-right">{t('integrations.etsy.ordersTable.total', 'Total')}</TableHead>
                      <TableHead className="text-right">{t('integrations.etsy.ordersTable.actions', 'Actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOrders.map((order, index) => {
                      const isSelected = order.id === selectedOrder?.id;
                      const isExpanded = expandedOrderIds.includes(order.id);

                      return (
                        <Fragment key={order.id}>
                          <TableRow
                            className={isSelected
                              ? 'cursor-pointer border-t bg-muted/35 shadow-[inset_3px_0_0_0_hsl(var(--primary))] transition-colors hover:bg-muted/45'
                              : 'cursor-pointer border-t bg-background transition-colors hover:bg-muted/20'}
                            onClick={() => setSelectedOrderId(order.id)}
                          >
                            <TableCell>
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  toggleOrderExpansion(order.id);
                                }}
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-4 w-4" />
                                ) : (
                                  <ChevronRight className="h-4 w-4" />
                                )}
                              </Button>
                            </TableCell>
                            <TableCell className="font-mono text-xs tabular-nums text-muted-foreground">
                              {String(index + 1).padStart(3, '0')}
                            </TableCell>
                            <TableCell>
                              <EtsyOrderPreviewThumbnail
                                order={order}
                              />
                            </TableCell>
                            <TableCell>{formatDateTime(order.importedAt)}</TableCell>
                            <TableCell>
                              <div className="flex flex-col gap-1">
                                <span className="text-sm font-medium text-foreground">
                                  #{order.etsyReceiptId}
                                </span>
                                <div className="flex flex-wrap items-center gap-2">
                                  {getEtsyProductionWarnings(order).length > 0 ? (
                                    <Badge variant="outline" className={getDetailTagClassName('warning')}>
                                      {t('integrations.etsy.toProduction.aspectRatioWarningBadge', 'Image ratio warning')}
                                    </Badge>
                                  ) : null}
                                  {(() => {
                                    const status = getOrderPrimaryStatus(order, t);
                                    return (
                                      <Badge variant="outline" className={getDetailTagClassName(status.tone)}>
                                        {status.label}
                                      </Badge>
                                    );
                                  })()}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-muted-foreground">{getAddressSummary(order)}</TableCell>
                            <TableCell className="text-right font-semibold">{formatMoney(order)}</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    onClick={(event) => event.stopPropagation()}
                                    aria-label={t('integrations.etsy.ordersTable.openActions', 'Open Etsy order actions')}
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.stopPropagation();
                                      requestOpenInProduction(order);
                                    }}
                                  >
                                    {t('integrations.etsy.actions.openInProduction', 'Open in production')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    onSelect={(event) => {
                                      event.preventDefault();
                                      void handleFetchOrderImages(order);
                                    }}
                                  >
                                    {t('integrations.etsy.actions.fetchImages', 'Fetch draft images')}
                                  </DropdownMenuItem>
                                  <DropdownMenuItem
                                    disabled={deletingOrderIds.includes(order.id)}
                                    onSelect={(event) => {
                                      event.preventDefault();
                                      void handleDeleteImportedOrder(order);
                                    }}
                                  >
                                    {deletingOrderIds.includes(order.id)
                                      ? t('integrations.etsy.actions.deletingOrder', 'Deleting…')
                                      : t('integrations.etsy.actions.deleteOrder', 'Delete order')}
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                          {isExpanded ? (
                            <TableRow key={`${order.id}-items`} className="bg-muted/5">
                              <TableCell colSpan={8} className="p-4">
                                <div className="space-y-4">
                                  <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                                    <span className={getDetailTagClassName('neutral')}>
                                      {t('integrations.etsy.ordersExpanded.orderItems', 'Order items')}
                                    </span>
                                    <span className="font-medium text-foreground">{order.shopName}</span>
                                    <span>{order.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')}</span>
                                    <span>{order.etsyData.buyer_email || t('integrations.etsy.common.noEmail', 'No email')}</span>
                                  </div>
                                  <div className="grid gap-3 md:grid-cols-2">
                                  {order.items.length > 0 ? order.items.map((item) => (
                                    <div
                                      key={item.transactionId}
                                      className="ml-4 flex items-start gap-3 rounded-lg border border-dashed border-border/60 bg-background/80 p-3"
                                    >
                                      <div className="h-16 w-16 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted/20">
                                        <img
                                          src={getPreferredEtsyItemPreviewUrl(item) || noImageWatercolor}
                                          alt={item.title}
                                          className="h-full w-full object-cover"
                                          loading="lazy"
                                        />
                                      </div>
                                      <div className="min-w-0 flex-1">
                                        <div className="truncate text-sm font-semibold text-foreground">
                                          {item.title}
                                        </div>
                                        <div className="mt-1 text-xs text-muted-foreground">
                                          {t('integrations.etsy.ordersExpanded.receiptLabel', 'Receipt #{{receiptId}}', { receiptId: order.etsyReceiptId })}
                                          {item.sku ? ` · ${t('integrations.etsy.ordersExpanded.skuLabel', 'SKU {{sku}}', { sku: item.sku })}` : ''}
                                          {' · '}
                                          {t('integrations.etsy.common.qty', 'Qty {{count}}', { count: item.quantity })}
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                          {t('integrations.etsy.ordersExpanded.etsyVariations', 'Etsy variations')}: {item.variationSummary.length > 0
                                            ? item.variationSummary.map((variation) => `${variation.name}: ${variation.value}`).join(' • ')
                                            : t('integrations.etsy.common.noEtsyVariations', 'No Etsy variations')}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {t('integrations.etsy.ordersExpanded.productionSize', 'Production size')}: {formatProductionSize(item, t)}
                                        </div>
                                        <div className="text-xs text-muted-foreground">
                                          {t('integrations.etsy.ordersExpanded.productionPaper', 'Production paper')}: {formatProductionPaper(item, t)}
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground">
                                          {getFullScaleImageStatusLabel(item, t)}
                                        </div>
                                        {getImageActionLabel(item.imageAvailability, t) ? (
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="mt-3 h-8"
                                            disabled={Boolean(currentFetchingImageItemId)}
                                            onClick={(event) => {
                                              event.stopPropagation();
                                              requestImageFetch(item.id, 'manual');
                                            }}
                                          >
                                            {currentFetchingImageItemId === item.id ? (
                                              <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                                            ) : null}
                                            {getImageActionLabel(item.imageAvailability, t)}
                                          </Button>
                                        ) : null}
                                      </div>
                                    </div>
                                  )) : (
                                    <div className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                                      {t('integrations.etsy.ordersExpanded.noItemDetails', 'No item-level transaction details were returned for this Etsy receipt.')}
                                    </div>
                                  )}
                                  </div>
                                </div>
                              </TableCell>
                            </TableRow>
                          ) : null}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </ScrollArea>
            ) : null}
          </CardContent>
        </Card>

        <Card className="min-h-0 border-border/70">
          <CardHeader>
            <CardTitle className="text-lg">
              {selectedOrder
                ? `Receipt #${selectedOrder.etsyReceiptId}`
                : t('integrations.etsy.orderDetailsTitle', 'Order details')}
            </CardTitle>
            <CardDescription>
              {selectedOrder
                ? t(
                    'integrations.etsy.orderDetailsDescription',
                    'Review the selected Etsy import and use AI chat for intake guidance before the order enters your normal workflow.'
                  )
                : t(
                    'integrations.etsy.orderDetailsEmpty',
                    'Select an Etsy order from the table to inspect it here.'
                  )}
            </CardDescription>
          </CardHeader>
          <CardContent className="min-h-0">
            {selectedOrder ? (
              <ScrollArea className="h-[calc(100vh-21rem)] pr-4">
                <div className="space-y-4">
                  <div className="grid gap-3 md:grid-cols-2">
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t('integrations.etsy.orderDetails.buyer', 'Buyer')}
                      </div>
                      <div className="mt-1 text-sm font-medium text-foreground">
                        {selectedOrder.etsyData.name || t('integrations.etsy.common.unknownBuyer', 'Unknown buyer')}
                      </div>
                      <div className="mt-1 inline-flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="h-3.5 w-3.5" />
                        {selectedOrder.etsyData.buyer_email || t('integrations.etsy.common.noEmail', 'No email')}
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-3">
                      <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {t('integrations.etsy.orderDetails.shop', 'Shop')}
                      </div>
                      <div className="mt-1 inline-flex items-center gap-2 text-sm font-medium text-foreground">
                        <Store className="h-3.5 w-3.5" />
                        {selectedOrder.shopName}
                      </div>
                      <div className="mt-1 text-sm text-muted-foreground">
                        {t('integrations.etsy.orderDetails.importedAt', 'Imported {{value}}', { value: formatDateTime(selectedOrder.importedAt) })}
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <div className="text-sm font-semibold text-foreground">{t('integrations.etsy.orderDetails.deliverySummary', 'Delivery summary')}</div>
                      <Badge variant="outline" className={getDetailTagClassName('neutral')}>
                        {selectedOrder.etsyData.country_iso}
                      </Badge>
                    </div>
                    <div className="space-y-2 text-sm text-muted-foreground">
                      <p>
                        {selectedOrder.etsyData.formatted_address
                          || [selectedOrder.etsyData.first_line, selectedOrder.etsyData.second_line]
                            .filter(Boolean)
                            .join(', ')
                          || t('integrations.etsy.orderDetails.noFormattedAddress', 'No formatted address')}
                      </p>
                      <p>{getAddressSummary(selectedOrder)}</p>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <div className="text-sm font-semibold text-foreground">
                          {t('integrations.etsy.openInProduction', 'Open in production flow')}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {t(
                            'integrations.etsy.openInProductionHelp',
                            'Silently park the current studio draft and continue this Etsy order in the production workspace.'
                          )}
                        </div>
                      </div>
                      <div className="flex justify-end">
                        <Button
                          disabled={openingOrderId === selectedOrder.id}
                          onClick={() => requestOpenInProduction(selectedOrder)}
                        >
                          {t('integrations.etsy.actions.openInProduction', 'Open in production')}
                          <ArrowRight className="ml-2 h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {selectedOrderDraft && shouldShowDraftStatus(selectedOrder) ? (
                    <div className="rounded-lg border border-border/60 bg-muted/20 p-4">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div>
                          <div className="text-sm font-semibold text-foreground">{t('integrations.etsy.orderDetails.draftSession', 'Draft session')}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('integrations.etsy.orderDetails.draftSessionDates', 'Last opened {{openedAt}} · Updated {{updatedAt}}', {
                              openedAt: formatDateTime(selectedOrderDraft.lastOpenedAt),
                              updatedAt: formatDateTime(selectedOrderDraft.updatedAt),
                            })}
                          </div>
                        </div>
                        <Badge variant="outline" className={getDetailTagClassName(getDraftStatusTagTone(selectedOrderDraft.status))}>
                          {getDraftStatusLabel(selectedOrder, t)}
                        </Badge>
                      </div>
                    </div>
                  ) : null}

                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="mb-3 text-sm font-semibold text-foreground">{t('integrations.etsy.orderDetails.importedItems', 'Imported Etsy items')}</div>
                    <div className="space-y-3">
                      {selectedOrder.items.length > 0 ? (
                        selectedOrder.items.map((item) => (
                          <div
                            key={item.transactionId}
                            className="rounded-lg border border-border/60 bg-muted/20 p-4"
                          >
                            <div className="flex flex-wrap items-start justify-between gap-3">
                              <div>
                                <div className="text-sm font-semibold text-foreground">
                                  {item.title}
                                </div>
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {t('integrations.etsy.orderDetails.transactionListing', 'Transaction #{{transactionId}} · Listing #{{listingId}}', {
                                    transactionId: item.transactionId,
                                    listingId: item.listingId,
                                  })}
                                  {item.sku ? ` · ${t('integrations.etsy.ordersExpanded.skuLabel', 'SKU {{sku}}', { sku: item.sku })}` : ''}
                                </div>
                              </div>
                              <Badge variant="outline" className={getDetailTagClassName('neutral')}>
                                {t('integrations.etsy.common.qty', 'Qty {{count}}', { count: item.quantity })}
                              </Badge>
                            </div>

                            {!item.imageAvailability.canDownload ? (
                              <div className="mt-4 overflow-hidden rounded-lg border border-border/60 bg-background/80">
                                <img
                                  src={noImageWatercolor}
                                  alt={t('integrations.etsy.images.noDraftImageAlt', 'No Etsy draft image available')}
                                  className="h-48 w-full object-cover"
                                />
                              </div>
                            ) : null}

                            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
                              <div className="rounded-md border border-border/50 bg-background/70 p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  {t('integrations.etsy.ordersExpanded.etsyVariations', 'Etsy variations')}
                                </div>
                                <div className="mt-1 text-sm text-foreground">
                                  {item.variationSummary.length > 0
                                    ? item.variationSummary.map((variation) => `${variation.name}: ${variation.value}`).join(' • ')
                                    : t('integrations.etsy.common.noEtsyVariations', 'No Etsy variations')}
                                </div>
                              </div>
                              <div className="rounded-md border border-border/50 bg-background/70 p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  {t('integrations.etsy.ordersExpanded.productionSize', 'Production size')}
                                </div>
                                <div className="mt-1 text-sm text-foreground">
                                  {formatProductionSize(item, t)}
                                </div>
                              </div>
                              <div className="rounded-md border border-border/50 bg-background/70 p-3">
                                <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                  {t('integrations.etsy.ordersExpanded.productionPaper', 'Production paper')}
                                </div>
                                <div className="mt-1 text-sm text-foreground">
                                  {formatProductionPaper(item, t)}
                                </div>
                              </div>
                            </div>

                            <div className="mt-3 rounded-md border border-border/50 bg-background/70 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('integrations.etsy.orderDetails.fullScaleImage', 'Full-scale image')}
                              </div>
                              <div className="mt-1 text-sm text-foreground">
                                {getFullScaleImageStatusLabel(item, t)}
                              </div>
                              {!item.fullSizeUploadedFile ? (
                                <div className="mt-1 text-xs text-muted-foreground">
                                  {t('integrations.etsy.orderDetails.fullScaleImageHelp', 'Upload the production full-size file. Etsy draft images are separate from this step.')}
                                </div>
                              ) : null}
                              {getImageActionLabel(item.imageAvailability, t) ? (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="mt-3"
                                  disabled={Boolean(currentFetchingImageItemId)}
                                  onClick={() => requestImageFetch(item.id, 'manual')}
                                >
                                  {currentFetchingImageItemId === item.id ? (
                                    <RefreshCw className="mr-2 h-3.5 w-3.5 animate-spin" />
                                  ) : null}
                                  {getImageActionLabel(item.imageAvailability, t)}
                                </Button>
                              ) : null}
                            </div>

                            <div className="mt-3 rounded-md border border-border/50 bg-background/70 p-3">
                              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                                {t('integrations.etsy.ordersExpanded.etsyVariations', 'Etsy variations')}
                              </div>
                              {item.variationSummary.length > 0 ? (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {item.variationSummary.map((variation) => (
                                    <Badge
                                      key={`${item.transactionId}-${variation.name}`}
                                      variant="outline"
                                      className={getDetailTagClassName('neutral')}
                                    >
                                      {variation.name}: {variation.value}
                                    </Badge>
                                  ))}
                                </div>
                              ) : (
                                <div className="mt-1 text-sm text-muted-foreground">
                                  {t('integrations.etsy.orderDetails.noVariationDetails', 'No variation details returned by Etsy for this item.')}
                                </div>
                              )}
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="rounded-md border border-dashed border-border bg-muted/20 px-4 py-6 text-sm text-muted-foreground">
                          {t('integrations.etsy.ordersExpanded.noItemDetails', 'No item-level transaction details were returned for this Etsy receipt.')}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="mb-3 text-sm font-semibold text-foreground">{t('integrations.etsy.orderDetails.intakeChecklist', 'Order intake checklist')}</div>
                    <div className="space-y-3">
                      <div className="flex items-start justify-between gap-3 rounded-md bg-muted/20 px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {t('integrations.etsy.orderDetails.originalFullScaleImages', 'Original full-scale images')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('integrations.etsy.orderDetails.originalFullScaleImagesHelp', 'Etsy draft images are fetched on demand from the inbox. Full original-file matching is still a later slice.')}
                          </div>
                        </div>
                        <Badge
                          variant="outline"
                          className={getDetailTagClassName(selectedOrder.imageDownloaded ? 'success' : 'warning')}
                        >
                          {selectedOrder.imageDownloaded
                            ? t('integrations.etsy.orderDetails.draftUploaded', 'Draft uploaded')
                            : t('integrations.etsy.orderDetails.needsFetch', 'Needs fetch')}
                        </Badge>
                      </div>
                      <div className="flex items-start justify-between gap-3 rounded-md bg-muted/20 px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-foreground">
                            {t('integrations.etsy.orderDetails.materialToPaper', 'Etsy material to paper mapping')}
                          </div>
                          <div className="text-xs text-muted-foreground">
                            {t('integrations.etsy.orderDetails.materialToPaperHelp', 'Reusable shop-level mapping rules are planned next.')}
                          </div>
                        </div>
                        <Badge variant="outline" className={getDetailTagClassName('warning')}>
                          {t('integrations.etsy.queue.pending', 'Pending')}
                        </Badge>
                      </div>
                      <div className="flex items-start justify-between gap-3 rounded-md bg-muted/20 px-3 py-2">
                        <div>
                          <div className="text-sm font-medium text-foreground">{t('integrations.etsy.orderDetails.aiAssistedReview', 'AI-assisted review')}</div>
                          <div className="text-xs text-muted-foreground">
                            {t('integrations.etsy.orderDetails.aiAssistedReviewHelp', 'The chat panel can already be used for intake guidance and exception handling.')}
                          </div>
                        </div>
                        <Badge variant="outline" className={`${getDetailTagClassName('success')} gap-1`}>
                          <Sparkles className="h-3.5 w-3.5" />
                          {t('integrations.etsy.readiness.ready', 'Ready')}
                        </Badge>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-lg border border-border/60 p-4">
                    <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
                      <AlertCircle className="h-4 w-4 text-amber-500" />
                      {t('integrations.etsy.orderDetails.firstPassFacts', 'First-pass intake facts')}
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('integrations.etsy.ordersTable.total', 'Total')}
                        </div>
                        <div className="mt-1 text-sm text-foreground">{formatMoney(selectedOrder)}</div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('integrations.etsy.orderDetails.paidShipped', 'Paid / shipped')}
                        </div>
                        <div className="mt-1 text-sm text-foreground">
                          {selectedOrder.etsyData.is_paid
                            ? t('integrations.etsy.orderDetails.paid', 'Paid')
                            : t('integrations.etsy.orderDetails.unpaid', 'Unpaid')} /{' '}
                          {selectedOrder.etsyData.is_shipped
                            ? t('integrations.etsy.orderDetails.shipped', 'Shipped')
                            : t('integrations.etsy.orderDetails.notShipped', 'Not shipped')}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('integrations.etsy.orderDetails.transactionCount', 'Transaction count')}
                        </div>
                        <div className="mt-1 text-sm text-foreground">
                          {getTransactionCount(selectedOrder)}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {t('integrations.etsy.orderDetails.giftMessage', 'Gift message')}
                        </div>
                        <div className="mt-1 text-sm text-foreground">
                          {selectedOrder.etsyData.gift_message || t('integrations.etsy.common.none', 'None')}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </ScrollArea>
            ) : (
              <div className="flex h-[calc(100vh-21rem)] items-center justify-center rounded-lg border border-dashed border-border bg-muted/20 text-sm text-muted-foreground">
                {t(
                  'integrations.etsy.orderDetailsEmpty',
                  'Select an Etsy order from the table to inspect it here.'
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <ConfirmAlertDialog
        open={confirmOrder !== null}
        onOpenChange={(open) => !open && setConfirmOrder(null)}
        title={t('integrations.etsy.toProduction.confirmTitle', 'Open with Etsy handoff warnings?')}
        description={confirmOrder ? [
          getMissingAreas(confirmOrder).length > 0
            ? t('integrations.etsy.toProduction.confirmDescription', 'This order will open in production with missing data: {{items}}. You can continue now and finish the missing matching later.', {
                items: getMissingAreas(confirmOrder).map((area) => getMissingAreaLabel(area, t)).join(', '),
              })
            : null,
          getEtsyProductionWarningSummary(getEtsyProductionWarnings(confirmOrder), t),
        ].filter(Boolean).join(' ') : ''}
        confirmText={t('integrations.etsy.toProduction.confirmButton', 'Open anyway')}
        cancelText={t('common.cancel', 'Cancel')}
        onConfirm={() => {
          if (confirmOrder) {
            void handleOpenInProduction(confirmOrder);
          }
        }}
      />
    </div>
  );
};

export default EtsyOrdersCanvas;
